import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { House, FileText, Users, ArrowsClockwise, Gear, SignOut, FileDashed, Receipt as ReceiptIcon, ChartBar, UsersThree, Truck, Warning, Signature, EnvelopeSimple } from "@phosphor-icons/react";

const nav = [
  { to: "/tableau-de-bord", label: "Tableau de bord", icon: House, testid: "nav-dashboard" },
  { to: "/factures", label: "Factures", icon: FileText, testid: "nav-invoices" },
  { to: "/soumissions", label: "Soumissions", icon: FileDashed, testid: "nav-quotes" },
  { to: "/contrats", label: "Contrats", icon: Signature, testid: "nav-contracts" },
  { to: "/courriels", label: "Courriels", icon: EnvelopeSimple, testid: "nav-emails" },
  { to: "/depenses", label: "Dépenses", icon: ReceiptIcon, testid: "nav-expenses" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, testid: "nav-suppliers" },
  { to: "/ecarts", label: "Écarts", icon: Warning, testid: "nav-disputes", badge: true },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/recurrences", label: "Récurrences", icon: ArrowsClockwise, testid: "nav-recurrences" },
  { to: "/rapports", label: "Rapports", icon: ChartBar, testid: "nav-reports" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UsersThree, testid: "nav-users", adminOnly: true },
  { to: "/parametres", label: "Paramètres", icon: Gear, testid: "nav-settings", adminOnly: true },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = nav.filter((n) => !n.adminOnly || user?.role === "admin");
  const [openDisputes, setOpenDisputes] = useState(0);

  useEffect(() => {
    api.get("/disputes?status=open").then((r) => setOpenDisputes(r.data.length)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground text-left">
      <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#0a0a0a] text-white fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <img src="/innova-fact-white.png" alt="innova-fact" className="h-11 w-auto" data-testid="brand-logo" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground font-medium" : "text-white/55 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={18} />
              <span>{n.label}</span>
              {n.badge && openDisputes > 0 && (
                <span data-testid="disputes-badge" className="ml-auto text-[10px] font-bold bg-white text-primary rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{openDisputes}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="px-3 pb-3">
            <div className="text-sm font-medium truncate text-white">{user?.name}</div>
            <div className="text-xs text-white/50 truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary mt-0.5">{user?.role === "admin" ? "Administrateur" : "Personnel"}</div>
          </div>
          <Button data-testid="logout-btn" variant="outline" size="sm"
            className="w-full rounded-sm justify-start bg-transparent border-white/15 text-white hover:bg-white/10 hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}>
            <SignOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <header className="md:hidden flex items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-4 py-3 sticky top-0 z-20">
          <img src="/innova-fact-white.png" alt="innova-fact" className="h-7 w-auto" />
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={async () => { await logout(); navigate("/login"); }}><SignOut size={18} /></Button>
        </header>
        <main className="p-5 md:p-8 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
